/**
 * Google Tag Manager Data Layer Utility
 * https://developers.google.com/tag-platform/tag-manager/datalayer
 *
 * This module provides type-safe functions for pushing e-commerce
 * and custom events to the GTM data layer AND directly to GA4 via gtag().
 *
 * Most helpers also dual-fire to PostHog so we have a single call site for
 * client-side analytics. PostHog is loaded lazily via posthog-js (already
 * initialized at app boot) — when it isn't ready we silently no-op.
 */

import posthog from "posthog-js";
import { getPostHogAttributionProperties } from "./attribution";
import { projectModeAnalyticsLabel } from "./projectModeLabels";
// dataLayer type is defined in global.d.ts

// Initialize dataLayer if it doesn't exist
if (typeof window !== "undefined") {
  window.dataLayer = window.dataLayer || [];
}

/**
 * `project_mode` carries the FROZEN internal ids ("studio" | "fast" |
 * "fast_v2") — never rename them, every saved insight and months of history
 * key on them. UI labels change over time (2026-07-08: fast_v2 is shown as
 * "Studio", studio as "Director"), so every event that carries project_mode
 * also gets a derived `project_mode_label` with the CURRENT display name.
 * New dashboards should filter on the label; old ones keep working on the id.
 */
function withProjectModeLabel<T extends object>(params: T): T {
  const record = params as Record<string, unknown>;
  const label = projectModeAnalyticsLabel(record?.project_mode);
  if (!label || record?.project_mode_label) return params;
  return { ...record, project_mode_label: label } as T;
}

/**
 * Push an event to the data layer (GTM)
 */
export function pushToDataLayer(data: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(withProjectModeLabel(data));
  console.log("[DataLayer] Pushed:", data);
}

/**
 * Loosely-typed analytics payload — every helper here builds an
 * object literal of unknown shape and forwards it. Using `object`
 * (instead of `Record<string, unknown>`) lets named interface types
 * like `UpgradeModalEventData` flow through without an explicit cast.
 */
type AnalyticsParams = object;

/**
 * Send an event directly to GA4 via gtag()
 */
function sendToGA4(eventName: string, params?: AnalyticsParams): void {
  if (typeof window === "undefined" || typeof window.gtag !== "function")
    return;
  window.__solmiLoadAnalytics?.();
  window.gtag(
    "event",
    eventName,
    (params ? withProjectModeLabel(params) : params) as Record<string, unknown>,
  );
  console.log("[GA4] Sent:", eventName, params);
}

/**
 * Best-effort PostHog capture. Wrapped in a try/catch so an analytics
 * failure can never break the app.
 */
function sendToPostHog(eventName: string, params?: AnalyticsParams): void {
  try {
    posthog.capture(eventName, {
      ...getPostHogAttributionProperties(),
      ...((params ? withProjectModeLabel(params) : params) as
        | Record<string, unknown>
        | undefined),
    });
  } catch (err) {
    console.warn("[PostHog] capture failed", eventName, err);
  }
}

/**
 * Clear the ecommerce object before pushing new ecommerce data
 * This is recommended by Google to prevent data from previous events
 * from being included in subsequent events
 */
export function clearEcommerce(): void {
  pushToDataLayer({ ecommerce: null });
}

// ============================================================
// E-COMMERCE EVENTS (GA4 format)
// https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
// ============================================================

interface EcommerceItem {
  item_id: string;
  item_name: string;
  price: number;
  quantity: number;
  item_category?: string;
  item_variant?: string;
}

interface PurchaseEventData {
  transaction_id: string;
  value: number;
  currency: string;
  items: EcommerceItem[];
  coupon?: string;
  affiliation?: string;
}

/**
 * Track a purchase event (conversion)
 * Call this on the checkout success page
 */
export function trackPurchase(data: PurchaseEventData): void {
  clearEcommerce();
  pushToDataLayer({
    event: "purchase",
    ecommerce: {
      transaction_id: data.transaction_id,
      value: data.value,
      currency: data.currency,
      items: data.items,
      ...(data.coupon && { coupon: data.coupon }),
      ...(data.affiliation && { affiliation: data.affiliation }),
    },
  });
  sendToGA4("purchase", {
    transaction_id: data.transaction_id,
    value: data.value,
    currency: data.currency,
    items: data.items,
    ...(data.coupon && { coupon: data.coupon }),
    ...(data.affiliation && { affiliation: data.affiliation }),
  });

  // Granular plan/top-up purchase events for funnel analysis. The base
  // `purchase` event above is the GA4 ecommerce conversion; these
  // additional events make it trivial to slice by Plus / Creator / Pro /
  // Top-up in both GA4 (custom events) and PostHog (insights/funnels).
  const firstItem = data.items[0];
  const itemId = firstItem?.item_id || "";
  const itemCategory = firstItem?.item_category || "";
  const planId = itemId.replace(/_(weekly|monthly|yearly)$/, "");
  const billingPeriod = itemId.endsWith("_yearly")
    ? "yearly"
    : itemId.endsWith("_weekly")
      ? "weekly"
      : itemId === "plus_trial"
        ? "trial"
        : itemId.endsWith("_monthly")
          ? "monthly"
          : undefined;

  let purchaseKind:
    | "plus"
    | "creator"
    | "pro"
    | "credit_topup"
    | "subscription"
    | "credits" = "subscription";
  if (itemCategory === "credits" || itemId.startsWith("credits_")) {
    purchaseKind = "credit_topup";
  } else if (planId === "plus" || planId === "plus_trial") {
    purchaseKind = "plus";
  } else if (planId === "creator") {
    purchaseKind = "creator";
  } else if (planId === "pro" || planId === "pro_v2") {
    purchaseKind = "pro";
  } else if (itemCategory === "subscription") {
    purchaseKind = "subscription";
  }

  const granularEventName =
    purchaseKind === "plus"
      ? "plus_plan_subscribed"
      : purchaseKind === "creator"
        ? "creator_plan_subscribed"
        : purchaseKind === "pro"
          ? "pro_plan_subscribed"
          : purchaseKind === "credit_topup"
            ? "credit_topup_purchased"
            : "subscription_purchased";

  const granularPayload = {
    transaction_id: data.transaction_id,
    value: data.value,
    currency: data.currency,
    plan_id: planId || undefined,
    billing_period: billingPeriod,
    item_id: itemId || undefined,
    item_name: firstItem?.item_name,
    quantity: firstItem?.quantity,
    purchase_kind: purchaseKind,
  };

  sendToGA4(granularEventName, granularPayload);
  sendToPostHog(granularEventName, granularPayload);

  // Plus/Pro get both a plan-specific event and the generic subscription event.
  if (
    purchaseKind !== "credit_topup" &&
    granularEventName !== "subscription_purchased"
  ) {
    sendToGA4("subscription_purchased", granularPayload);
    sendToPostHog("subscription_purchased", granularPayload);
  }

  // Google Ads imports purchase/subscription/top-up events from GA4. Keep this
  // file GA4-only so Ads has a single conversion data source.
}

/**
 * Track when user views a product/plan
 */
export function trackViewItem(
  item: EcommerceItem,
  currency: string = "USD",
): void {
  clearEcommerce();
  pushToDataLayer({
    event: "view_item",
    ecommerce: {
      currency,
      value: item.price,
      items: [item],
    },
  });
  sendToGA4("view_item", {
    currency,
    value: item.price,
    items: [item],
  });
}

let _lastCheckoutKey = "";
let _lastCheckoutTs = 0;

/**
 * Track when user begins checkout
 */
export function trackBeginCheckout(
  items: EcommerceItem[],
  value: number,
  currency: string = "USD",
  context?: { trigger?: string; source?: string },
): void {
  // Dedup: skip if the same item was tracked within the last 30s
  const dedupeKey = items.map((i) => i.item_id).join("|");
  const now = Date.now();
  if (dedupeKey === _lastCheckoutKey && now - _lastCheckoutTs < 30_000) return;
  _lastCheckoutKey = dedupeKey;
  _lastCheckoutTs = now;

  clearEcommerce();
  pushToDataLayer({
    event: "begin_checkout",
    ecommerce: {
      currency,
      value,
      items,
    },
    ...(context?.trigger && { trigger: context.trigger }),
    ...(context?.source && { source: context.source }),
  });
  sendToGA4("begin_checkout", {
    currency,
    value,
    items,
    ...(context?.trigger && { trigger: context.trigger }),
    ...(context?.source && { source: context.source }),
  });
  // Plan-aware PostHog event so we can build per-plan funnels without
  // having to parse out the GA4 ecommerce items array.
  const firstItem = items[0];
  const itemId = firstItem?.item_id || "";
  const planId = itemId.replace(/_(weekly|monthly|yearly)$/, "");
  const billingPeriod = itemId.endsWith("_yearly")
    ? "yearly"
    : itemId.endsWith("_weekly")
      ? "weekly"
      : itemId === "plus_trial"
        ? "trial"
        : itemId.endsWith("_monthly")
          ? "monthly"
          : undefined;
  const isCreditTopup =
    firstItem?.item_category === "credits" || itemId.startsWith("credits_");
  sendToPostHog("checkout_started", {
    value,
    currency,
    plan_id: planId || undefined,
    billing_period: billingPeriod,
    item_id: itemId || undefined,
    item_name: firstItem?.item_name,
    purchase_kind: isCreditTopup
      ? "credit_topup"
      : planId === "plus" || planId === "plus_trial"
        ? "plus"
        : planId === "creator"
          ? "creator"
          : planId === "pro" || planId === "pro_v2"
            ? "pro"
            : "subscription",
    ...(context?.trigger && { trigger: context.trigger }),
    ...(context?.source && { source: context.source }),
  });
}

// ============================================================
// CUSTOM EVENTS
// ============================================================

/**
 * Track user sign up. Primary optimization target as of the 2026-05-10
 * SEM strategy revision.
 *
 * Google Ads imports this event from GA4. Do not emit a native Ads conversion
 * here or via server-side upload-clicks, otherwise Ads and GA4 diverge again.
 */
export function trackSignUp(method: string = "email"): void {
  pushToDataLayer({
    event: "sign_up",
    method,
  });
  sendToGA4("sign_up", { method });
}

/**
 * Track user login
 */
export function trackLogin(method: string = "email"): void {
  pushToDataLayer({
    event: "login",
    method,
  });
  sendToGA4("login", { method });
}

/**
 * Track song generation
 */
export function trackSongGeneration(genre?: string): void {
  pushToDataLayer({
    event: "generate_song",
    content_type: "song",
    ...(genre && { genre }),
  });
  sendToGA4("generate_song", {
    content_type: "song",
    ...(genre && { genre }),
  });
}

/**
 * Track feature usage
 */
export function trackFeatureUsage(
  featureName: string,
  details?: Record<string, unknown>,
): void {
  pushToDataLayer({
    event: "feature_usage",
    feature_name: featureName,
    ...details,
  });
  sendToGA4("feature_usage", {
    feature_name: featureName,
    ...details,
  });
}

/**
 * Track a user's attempt to start the music-video generator. This is the
 * click/attempt event, not the later project-created lifecycle event.
 */
export function trackTryGenerateVideo(
  data: {
    source: "home_page" | "ai_music_video_generator" | "landing_page" | string;
    trigger?: string;
    track_id?: string;
    has_audio?: boolean;
    project_mode?: "studio" | "fast" | "fast_v2";
    aspect_ratio?: string;
    resolution?: string;
  } = { source: "unknown" },
): void {
  pushToDataLayer({
    event: "try_to_generate_video",
    ...data,
  });
  sendToGA4("try_to_generate_video", data);
  sendToPostHog("try_to_generate_video", data);

  // `activation_video` is the product-funnel name used by the partner
  // dashboard. Keep the historical event above for existing GA4 goals and
  // dashboards while dual-emitting the clearer name for new reporting.
  pushToDataLayer({
    event: "activation_video",
    ...data,
  });
  sendToGA4("activation_video", data);
  sendToPostHog("activation_video", data);
}

/**
 * Track a user's attempt to start the lyric-video generator. Kept separate from
 * `try_to_generate_video` so music-video project intent is not inflated by
 * lyric-video traffic.
 */
export function trackTryGenerateLyricVideo(
  data: {
    source: "home_page" | "ai_music_video_generator" | "landing_page" | string;
    trigger?: string;
    track_id?: string;
    has_audio?: boolean;
    aspect_ratio?: string;
    resolution?: string;
    background_type?: string;
  } = { source: "unknown" },
): void {
  pushToDataLayer({
    event: "try_to_generate_lyric_video",
    ...data,
  });
  sendToGA4("try_to_generate_lyric_video", data);
  sendToPostHog("try_to_generate_lyric_video", data);
}

/**
 * Track page view with custom parameters
 */
export function trackPageView(pagePath: string, pageTitle?: string): void {
  pushToDataLayer({
    event: "page_view",
    page_path: pagePath,
    ...(pageTitle && { page_title: pageTitle }),
  });
  sendToGA4("page_view", {
    page_path: pagePath,
    ...(pageTitle && { page_title: pageTitle }),
  });
}

// ============================================================
// UPGRADE / PAYWALL EVENTS
// ============================================================

interface UpgradeModalEventData {
  trigger?: string;
  source?: string;
  defaultTab?: string;
  currentPlan?: string;
}

/** Fire when the upgrade modal appears on screen. */
export function trackUpgradeModalShown(data: UpgradeModalEventData = {}): void {
  pushToDataLayer({
    event: "upgrade_modal_shown",
    ...data,
  });
  sendToGA4("upgrade_modal_shown", data);
  sendToPostHog("upgrade_modal_shown", data);
}

/**
 * Fire when a free user tries to switch a project public→private and hits the
 * paid-only gate (the upgrade popup opens immediately after). The privacy
 * paywall otherwise collapses into the generic `director_pipeline`
 * upgrade_modal_shown bucket — this dedicated event makes the
 * "tried private → saw popup → subscribed" funnel measurable.
 */
export function trackPrivateProjectPaywall(
  data: { source?: string; currentPlan?: string } = {},
): void {
  pushToDataLayer({
    event: "private_project_paywall_hit",
    ...data,
  });
  sendToGA4("private_project_paywall_hit", data);
  sendToPostHog("private_project_paywall_hit", data);
}

/** Fire when the user dismisses the upgrade modal without converting. */
export function trackUpgradeModalDismissed(
  data: UpgradeModalEventData = {},
): void {
  pushToDataLayer({
    event: "upgrade_modal_dismissed",
    ...data,
  });
  sendToGA4("upgrade_modal_dismissed", data);
  sendToPostHog("upgrade_modal_dismissed", data);
}

type TrialMembershipOfferEventData = {
  trigger?: string;
  source?: string;
  item_id?: string;
  item_name?: string;
  offer_type?: string;
  popup_variant?: string;
  popup_stage?: string;
  popup_placement?: string;
  trial_days?: number;
  credits?: number;
  click_target?: string;
  action_label?: string;
  dismiss_reason?: string;
  value?: number;
  currency?: string;
};

/**
 * GA4's standard Events report can only split rows by event NAME, so the
 * two trial-offer surfaces (duration-choice popup vs post-render claim)
 * are indistinguishable there — popup_variant is just a parameter. To make
 * them separable in the GA UI we ALSO fire a GA4-only alias event whose
 * name encodes the surface, e.g. `trial_offer_shown_post_render`.
 * The canonical event names stay untouched (PostHog queries, GTM triggers,
 * and GA history all key on them).
 */
const TRIAL_OFFER_GA4_ACTION: Record<string, string> = {
  trial_membership_offer_shown: "shown",
  trial_membership_offer_clicked: "clicked",
  trial_membership_offer_dismissed: "dismissed",
};

const TRIAL_OFFER_GA4_VARIANT: Record<string, string> = {
  duration_choice: "duration",
  post_render_claim: "post_render",
  topup_claim: "topup",
  resume_claim: "resume",
};

function trackTrialMembershipOfferEvent(
  eventName: string,
  data: TrialMembershipOfferEventData = {},
): void {
  const payload = {
    source: "trial_membership_popup",
    item_id: "plus_trial",
    item_name: "New Member Plus Trial",
    offer_type: "new_user_weekly_trial",
    popup_variant: "trial_membership_popup_v2",
    trial_days: 7,
    credits: 500,
    value: 9.9,
    currency: "USD",
    ...data,
  };

  pushToDataLayer({
    event: eventName,
    ...payload,
  });
  sendToGA4(eventName, payload);
  const aliasAction = TRIAL_OFFER_GA4_ACTION[eventName];
  const aliasVariant = TRIAL_OFFER_GA4_VARIANT[payload.popup_variant];
  if (aliasAction && aliasVariant) {
    sendToGA4(`trial_offer_${aliasAction}_${aliasVariant}`, payload);
  }
  sendToPostHog(eventName, payload);
}

export function trackTrialMembershipOfferShown(
  data: TrialMembershipOfferEventData = {},
): void {
  trackTrialMembershipOfferEvent("trial_membership_offer_shown", data);
}

export function trackTrialMembershipOfferClicked(
  data: TrialMembershipOfferEventData = {},
): void {
  trackTrialMembershipOfferEvent("trial_membership_offer_clicked", data);
}

export function trackTrialMembershipPopupClick(
  data: TrialMembershipOfferEventData = {},
): void {
  trackTrialMembershipOfferEvent("trial_membership_popup_click", data);
}

export function trackTrialMembershipOfferDismissed(
  data: TrialMembershipOfferEventData = {},
): void {
  trackTrialMembershipOfferEvent("trial_membership_offer_dismissed", data);
}

export function trackTrialMembershipRulesOpened(
  data: TrialMembershipOfferEventData = {},
): void {
  trackTrialMembershipOfferEvent("trial_membership_rules_opened", data);
}

// ============================================================
// MUSIC VIDEO PROJECT LIFECYCLE
// ============================================================
//
// The Director / DirectorHome flow has a long-running multi-step
// pipeline (analyze → style → brief → refs → shots → storyboard →
// render → complete). We emit a small set of canonical events so we
// can measure drop-off, error rates, and end-to-end success in both
// GA4 and PostHog. Each event carries `project_id` so funnel analysis
// can stitch the same project across steps.

interface VideoProjectEventBase {
  project_id?: string;
  source?: string;
  aspect_ratio?: string;
  resolution?: string;
  style?: string;
  project_mode?: "studio" | "fast" | "fast_v2";
  has_audio?: boolean;
  has_character_image?: boolean;
  has_vibe_image?: boolean;
}

type VideoCopyrightConfirmationEventData = VideoProjectEventBase & {
  trigger?: string;
  output_type?: string;
  is_public?: boolean;
};

function trackVideoCopyrightConfirmationEvent(
  eventName: string,
  data: VideoCopyrightConfirmationEventData = {},
): void {
  pushToDataLayer({
    event: eventName,
    ...data,
  });
  sendToGA4(eventName, data);
  sendToPostHog(eventName, data);
}

export function trackVideoCopyrightConfirmationShown(
  data: VideoCopyrightConfirmationEventData = {},
): void {
  trackVideoCopyrightConfirmationEvent(
    "video_copyright_confirmation_shown",
    data,
  );
}

export function trackVideoCopyrightConfirmationCancelled(
  data: VideoCopyrightConfirmationEventData = {},
): void {
  trackVideoCopyrightConfirmationEvent(
    "video_copyright_confirmation_cancelled",
    data,
  );
}

export type VideoProjectFunnelStep =
  | "music_analysis"
  | "visual_style"
  | "creative_brief"
  | "references"
  | "shot_list"
  | "storyboard"
  | "render";

const VIDEO_PROJECT_FUNNEL_STEP_EVENTS: Record<VideoProjectFunnelStep, string> =
  {
    music_analysis: "music_analysis_complete",
    visual_style: "visual_style_complete",
    creative_brief: "creative_brief_complete",
    references: "references_complete",
    shot_list: "shot_list_complete",
    storyboard: "storyboard_complete",
    render: "render_complete",
  };

const VIDEO_PROJECT_FUNNEL_PIPELINE_STEPS: Record<
  VideoProjectFunnelStep,
  string
> = {
  music_analysis: "analyzing",
  visual_style: "styling",
  creative_brief: "briefing",
  references: "references",
  shot_list: "shotlist",
  storyboard: "storyboard",
  render: "rendering",
};

const VIDEO_PROJECT_FUNNEL_STEP_LABELS: Record<VideoProjectFunnelStep, string> =
  {
    music_analysis: "Music Analysis",
    visual_style: "Visual Style",
    creative_brief: "Creative Brief",
    references: "References",
    shot_list: "Shot List",
    storyboard: "Storyboard",
    render: "Render",
  };

/** User clicked "Direct Video" / project creation kicked off. */
export function trackVideoProjectInitiated(
  data: VideoProjectEventBase = {},
): void {
  pushToDataLayer({
    event: "video_project_initiated",
    ...data,
  });
  sendToGA4("video_project_initiated", data);
  sendToPostHog("video_project_initiated", data);
}

/**
 * One attempt to edit an existing storyboard frame or rendered clip.
 *
 * `surface` distinguishes the deterministic editor UI from the chat agent;
 * `pointed` marks chat edits where the user named the shot ("@Shot S1.3" or
 * a chip like "Yes, fix S1.5") vs. freeform asks ("make it look better").
 * Feeds the retry-until-abandon comparison between the two edit flows:
 * group by (project_id, scene_num, shot_num), count consecutive attempts,
 * check whether a render/export follows the last one.
 */
export function trackStoryboardEditAttempt(data: {
  surface: "editor_modal" | "chat";
  edit_kind: "frame_image" | "video_clip" | "performer_change";
  pointed: boolean;
  ok: boolean;
  project_id?: string;
  scene_num?: number;
  shot_num?: number;
  has_instruction?: boolean;
  instruction_len?: number;
  error?: string;
  [k: string]: unknown;
}): void {
  const payload = {
    ...data,
    error: data.error ? String(data.error).slice(0, 200) : undefined,
  };
  pushToDataLayer({ event: "storyboard_edit_attempt", ...payload });
  sendToGA4("storyboard_edit_attempt", payload);
  sendToPostHog("storyboard_edit_attempt", payload);
}

/**
 * Pipeline step transition. `step` matches the Director state machine
 * (analyzing | styling | briefing | references | shotlist | storyboard
 * | rendering | complete). `phase` is "started" or "completed".
 */
export function trackVideoProjectStep(
  step: string,
  phase: "started" | "completed",
  data: VideoProjectEventBase & Record<string, unknown> = {},
): void {
  const payload = { step, phase, ...data };
  pushToDataLayer({
    event: "video_project_step",
    ...payload,
  });
  sendToGA4("video_project_step", payload);
  // Not sent to PostHog (Aug 2026 cost control): it re-fires on every Director
  // remount and the dedicated *_complete step events already cover PostHog
  // funnels; GA4 keeps the unified step stream.
}

/**
 * Step-specific completion events for the visible music-video generation
 * funnel. These preserve the generic `video_project_step` event for unified
 * funnels, and also emit dedicated GA/PostHog events such as
 * `music_analysis_complete` and `storyboard_complete` for simple reporting.
 */
export function trackVideoProjectFunnelStepCompleted(
  step: VideoProjectFunnelStep,
  data: VideoProjectEventBase & {
    status?: "done" | "error";
    duration_ms?: number;
    [k: string]: unknown;
  } = {},
): void {
  const eventName = VIDEO_PROJECT_FUNNEL_STEP_EVENTS[step];
  const pipelineStep = VIDEO_PROJECT_FUNNEL_PIPELINE_STEPS[step];
  const status = data.status ?? "done";
  const commonPayload = {
    funnel: "music_video_generation",
    funnel_step: step,
    step_label: VIDEO_PROJECT_FUNNEL_STEP_LABELS[step],
    status,
    event_timestamp: new Date().toISOString(),
    ...data,
  };

  trackVideoProjectStep(pipelineStep, "completed", commonPayload);

  const stepPayload = {
    ...commonPayload,
    step,
    phase: "completed",
  };
  pushToDataLayer({
    event: eventName,
    ...stepPayload,
  });
  sendToGA4(eventName, stepPayload);
  sendToPostHog(eventName, stepPayload);
}

/**
 * Extract the balance/price pair from the server's credit-gate messages —
 * both director shapes ("Insufficient credits. You have N music-video usable
 * credits (…) but need M credits for …" and the storyboard job's "You have N
 * … but need M credits (K short after starter video credits)"). Returns null
 * when the message carries no parseable numbers.
 */
export function parseCreditGateNumbers(message: string): {
  currentCredits: number;
  neededCredits: number;
  shortfall: number;
} | null {
  const match = message.match(
    /you have (\d+)[\s\S]{0,160}?need (\d+) credits/i,
  );
  if (!match) return null;
  const currentCredits = Number(match[1]);
  const neededCredits = Number(match[2]);
  return {
    currentCredits,
    neededCredits,
    shortfall: Math.max(0, neededCredits - currentCredits),
  };
}

/**
 * A subscription/paywall gate (e.g. the free music-video trial cap) is not a
 * failure — it's an expected upsell moment. Logging it as
 * `video_project_error` pollutes error metrics, so these are routed to a
 * distinct `video_paywall_hit` event instead.
 */
function isPaywallGate(errorMessage: string, errorCode?: string): boolean {
  // Provider balance failures ("WaveSpeed POST failed (400): Insufficient
  // credits") are OUR vendor account draining — a real outage that must stay
  // in error metrics, not an upsell moment.
  if (
    /wavespeed|pixverse|byteplus|alibaba|replicate|poll failed|post failed/i.test(
      errorMessage,
    )
  ) {
    return false;
  }
  if (
    errorCode === "subscription_required" ||
    errorCode === "free_trial_capped" ||
    // Running out of credits opens the top-up/upgrade modal — an upsell
    // moment, not an application error.
    errorCode === "insufficient_credits"
  ) {
    return true;
  }
  return /free music video trial is capped|subscription required|insufficient credits/i.test(
    errorMessage,
  );
}

/**
 * A background Director job (music analysis, shot list, render) that is still
 * processing surfaces a "still running … try again in a moment" message when a
 * poll lands before the job finishes. That is an expected in-progress state, not
 * a failure — the client polls again and the step ultimately succeeds. Logging
 * it as `video_project_error` badly inflates error metrics (it was ~68% of all
 * `video_project_error` events), so these are routed to a distinct
 * `video_project_pending` event instead.
 */
function isTransientPending(errorMessage: string, errorCode?: string): boolean {
  if (errorCode === "job_still_running" || errorCode === "job_pending") {
    return true;
  }
  return /still running|not available yet|try again in a moment/i.test(
    errorMessage,
  );
}

/**
 * An error occurred at any step. `step` identifies where; `error_message`
 * is the user-facing message; `error_code` is optional structured tag
 * (e.g. "insufficient_credits", "render_failed"). Subscription/paywall gates
 * are re-routed to `video_paywall_hit`, and transient "still running" poll
 * states to `video_project_pending`, so neither inflates error metrics.
 */
export function trackVideoProjectError(
  step: string,
  errorMessage: string,
  data: VideoProjectEventBase & {
    error_code?: string;
    [k: string]: unknown;
  } = {},
): void {
  const truncated = errorMessage.slice(0, 300);
  const errorCode =
    typeof data.error_code === "string" ? data.error_code : undefined;

  // Paywall/subscription gates are expected upsell moments, not errors.
  if (isPaywallGate(errorMessage, errorCode)) {
    // Both credit-gate messages ("You have N … but need M credits …") carry
    // the numbers this funnel is analyzed by — surface them as structured
    // props so shortfall size is queryable without parsing message text.
    const credits = parseCreditGateNumbers(errorMessage);
    const paywallPayload = {
      step,
      reason: errorCode || "message_match",
      ...(credits
        ? {
            current_credits: credits.currentCredits,
            needed_credits: credits.neededCredits,
            shortfall_credits: credits.shortfall,
          }
        : {}),
      ...data,
    };
    pushToDataLayer({
      event: "video_paywall_hit",
      ...paywallPayload,
    });
    sendToGA4("video_paywall_hit", paywallPayload);
    sendToPostHog("video_paywall_hit", paywallPayload);
    return;
  }

  // A background job that is still processing is an expected poll state, not a
  // failure — route it away from error metrics.
  if (isTransientPending(errorMessage, errorCode)) {
    const pendingPayload = { step, error_message: truncated, ...data };
    pushToDataLayer({
      event: "video_project_pending",
      ...pendingPayload,
    });
    sendToGA4("video_project_pending", pendingPayload);
    sendToPostHog("video_project_pending", pendingPayload);
    return;
  }

  const payload = {
    step,
    error: truncated,
    error_message: truncated,
    ...data,
  };
  pushToDataLayer({
    event: "video_project_error",
    ...payload,
  });
  sendToGA4("video_project_error", payload);
  sendToPostHog("video_project_error", payload);
}

/**
 * A Director chat tool call settled without doing what the agent asked:
 * either it returned ok:false (client guard, validation, server rejection)
 * or it reported a "Recovered from a network interruption" result. These
 * were previously invisible — SUP-GCXOWBPP had six silent generate_shot_list
 * failures with zero telemetry. PostHog-only (low volume, debugging signal,
 * not a marketing funnel event).
 */
export function trackDirectorChatToolSettledAbnormally(
  data: VideoProjectEventBase & {
    tool: string;
    error_message?: string;
    recovered?: boolean;
  },
): void {
  const message = data.error_message || "";
  const category = data.recovered
    ? "recovered"
    : isPaywallGate(message)
      ? "paywall"
      : isTransientPending(message)
        ? "pending"
        : "error";
  const payload = {
    ...data,
    category,
    error_message: message.slice(0, 300) || undefined,
  };
  sendToPostHog("director_chat_tool_failed", payload);
}

/** Final render finished successfully — fire once per project. */
export function trackVideoProjectCompleted(
  data: VideoProjectEventBase & {
    clip_count?: number;
    rendered_now?: number;
    duration_sec?: number;
  } = {},
): void {
  pushToDataLayer({
    event: "video_project_completed",
    ...data,
  });
  sendToGA4("video_project_completed", data);
  sendToPostHog("video_project_completed", data);
}

export function trackVideoPlaybackStarted(
  data: VideoProjectEventBase & {
    clip_index?: number;
    duration_sec?: number;
    source?: string;
  } = {},
): void {
  pushToDataLayer({
    event: "video_playback_started",
    ...data,
  });
  sendToGA4("video_playback_started", data);
  sendToPostHog("video_playback_started", data);
}
